"use client";

import { c as _c } from "react/compiler-runtime";
import { useSyncExternalStore } from "react";
type ChatBotNavigationListener = () => void;
type ChatBotHistoryState = {
  __chatBotModal?: {
    isOpen?: boolean;
  };
  __N?: boolean;
  options?: Record<string, unknown>;
  [key: string]: unknown;
};
const CHATBOT_HISTORY_STATE_KEY = "__chatBotModal";
const chatBotNavigationListeners = new Set<ChatBotNavigationListener>();
let chatBotIsOpen = false;
const getChatBotHistoryState = (): ChatBotHistoryState => {
  const {
    state
  } = window.history;
  return state && typeof state === "object" ? state as ChatBotHistoryState : {};
};
const preservePageRouterScroll = (state: ChatBotHistoryState) => {
  // Next.js marks Pages Router history entries with __N. App Router entries
  // use a different marker, so leave those entries unchanged.
  if (!state.__N) return state;

  // Pages Router normally scrolls to the top after a popstate. Prevent that
  // reset when closing the chatbot and returning to this history entry.
  return {
    ...state,
    options: {
      ...state.options,
      scroll: false
    }
  };
};
const isChatBotHistoryEntryOpen = () => Boolean(getChatBotHistoryState()[CHATBOT_HISTORY_STATE_KEY]?.isOpen);
const getChatBotNavigationSnapshot = () => chatBotIsOpen;
const getServerChatBotNavigationSnapshot = () => false;
const notifyChatBotNavigationChange = () => {
  for (const listener of chatBotNavigationListeners) listener();
};
const setChatBotIsOpen = (isOpen: boolean) => {
  if (chatBotIsOpen === isOpen) return;
  chatBotIsOpen = isOpen;
  notifyChatBotNavigationChange();
};
const synchronizeChatBotWithHistory = () => {
  setChatBotIsOpen(isChatBotHistoryEntryOpen());
};
const subscribeToChatBotNavigation = (listener: ChatBotNavigationListener) => {
  const isFirstSubscriber = chatBotNavigationListeners.size === 0;
  chatBotNavigationListeners.add(listener);
  if (isFirstSubscriber) {
    window.addEventListener("popstate", synchronizeChatBotWithHistory);
    synchronizeChatBotWithHistory();
  }
  return () => {
    chatBotNavigationListeners.delete(listener);
    if (chatBotNavigationListeners.size === 0) {
      window.removeEventListener("popstate", synchronizeChatBotWithHistory);
    }
  };
};
const openChatBot = () => {
  if (chatBotIsOpen) return;
  const currentHistoryState = getChatBotHistoryState();
  // Store scroll: false on the current Pages Router entry before adding the
  // chatbot entry, so history.back() does not move the page to the top.
  const historyState = preservePageRouterScroll(currentHistoryState);
  if (historyState !== currentHistoryState) {
    // replaceState keeps the same URL and history position; it only updates
    // the state that Next.js will read when this entry is restored.
    window.history.replaceState(historyState, "", window.location.href);
  }
  window.history.pushState({
    ...historyState,
    [CHATBOT_HISTORY_STATE_KEY]: {
      isOpen: true
    }
  }, "", window.location.href);
  setChatBotIsOpen(true);
};
const closeChatBot = () => {
  if (!chatBotIsOpen) return;
  window.history.back();
};

// Internal links can reload the same URL (for example, "/" from "/"). In
// that case the browser keeps the current history state, so mark this entry
// closed before the link navigation happens.
const dismissChatBot = () => {
  if (!chatBotIsOpen) return;
  window.history.replaceState({
    ...getChatBotHistoryState(),
    [CHATBOT_HISTORY_STATE_KEY]: {
      isOpen: false
    }
  }, "", window.location.href);
  setChatBotIsOpen(false);
};
const toggleChatBot = () => {
  if (chatBotIsOpen) {
    closeChatBot();
    return;
  }
  openChatBot();
};
export const useChatBotNavigation = () => {
  const $ = _c(2);
  const isOpen = useSyncExternalStore(subscribeToChatBotNavigation, getChatBotNavigationSnapshot, getServerChatBotNavigationSnapshot);
  let t0;
  if ($[0] !== isOpen) {
    t0 = {
      close: closeChatBot,
      dismiss: dismissChatBot,
      isOpen,
      open: openChatBot,
      toggle: toggleChatBot
    };
    $[0] = isOpen;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};